import { userLogin, UserLoginParams, userRegister, UserRegisterParams } from '@/service/api/user'
import { defineStore, storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { serverMessage } from '@/service'
import { RoleId } from '@rapoo/device-sdk'
import { useAdminMode } from '@/composables/useAdminOnly/adminMode'
import { useDeviceStore } from './device'

/**
 * 用户状态管理 Store
 * 负责管理用户登录状态和基本信息
 */
export const useUserStore = defineStore(
  'user',
  () => {
    // State
    const isLoggedIn = ref(false)

    interface UserInfo {
      id: string
      username: string // 账号
      nickname: string // 昵称
      email: string
      token?: string
      isBindVx?: boolean
      roleIds?: number[]
    }

    const userInfo = ref<UserInfo | null>(null)

    const { adminLogin, adminLogout, isLoading } = useAdminMode()

    // Actions
    /**
     * 登录
     */
    async function login(data: UserLoginParams) {
      try {
        const res = await userLogin(data)
        if (res.code === 0) {
          const { data } = res
          setUserInfo(data)
          if (isAdmin.value) {
            adminLogin()
          }
        } else {
          const message = serverMessage(res.code, res.msg)
          throw new Error(message)
        }
      } catch (error) {
        console.error('登录请求失败:', error)
        throw error
      }
    }

    const scanLogin = (data: UserInfo) => {
      setUserInfo(data)
    }

    const updateUserInfo = (data: UserInfo) => {
      if (userInfo.value) {
        userInfo.value = { ...userInfo.value, ...data }
      }
    }

    const setUserInfo = (data: UserInfo) => {
      userInfo.value = data
      isLoggedIn.value = true
    }

    const register = async (data: UserRegisterParams) => {
      try {
        const res = await userRegister(data)
        if (res.code === 0) {
          const loginRes = await userLogin({
            username: data.username,
            password: data.password,
          })
          if (loginRes.code === 0) {
            const { data: loginData } = loginRes
            setUserInfo(loginData)
            if (isAdmin.value) {
              adminLogin()
            }
          }
        } else {
          const message = serverMessage(res.code, res.msg)
          throw new Error(message)
        }
      } catch (error: any) {
        console.error('注册请求失败:', error)
        throw error
      }
    }

    /**
     * 退出登录
     */
    async function logout() {
      const shouldExitAdmin = isAdmin.value
      isLoggedIn.value = false
      userInfo.value = null

      if (shouldExitAdmin) {
        await adminLogout()
        return
      }

      // 未登录状态按普通用户槽位解释；空角色会被 SDK 视为“全部槽位”。
      const deviceStore = useDeviceStore()
      const { expandedDevices } = storeToRefs(deviceStore)
      for (const item of expandedDevices.value) {
        item.original.setConfigSlotRoles([RoleId.user])
      }
    }

    /**
     * 判断是否是管理员账号
     * 1-超级管理员
     * 2-管理员
     * 3-普通用户
     */
    const adminRoles: number[] = [RoleId.admin, RoleId.superAdmin]
    const isAdmin = computed(() => {
      return userInfo.value?.roleIds?.some((id) => adminRoles.includes(id))
    })

    return {
      isLoggedIn,
      userInfo,
      isAdmin,
      isLoading,
      updateUserInfo,
      login,
      scanLogin,
      logout,
      register,
    }
  },
  {
    // 状态持久化：只持久化用户信息，排除 loading 状态
    // isLoading 是 useLoading 的 ref，会被自动解包成 store state 并持久化。
    // 若在 adminLogin 执行期间（isLoading=true 窗口期）刷新页面，localStorage 会存下
    // isLoading: true，下次启动 hydrate 后页面将永远 loading。
    persist: {
      omit: ['isLoading'],
    },
  },
)
